import BusinessDataReview from "../myinfo-business/BusinessDataReview";
import React from "react";
import { CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloseIcon from "@mui/icons-material/Close";
import "./CrmSyncReview.css";

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  return String(value);
};

const money = (value) => {
  const amount = Number(value);
  return `SGD ${Number.isFinite(amount) ? amount.toLocaleString("en-SG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) : "0.00"}`;
};

const Field = ({ label, value }) => (
  <div className="crm-review-field">
    <dt>{label}</dt>
    <dd>{displayValue(value)}</dd>
  </div>
);

const Fields = ({ items }) => (
  <dl className="crm-review-fields">
    {items.map(([label, value]) => (
      <Field key={label} label={label} value={value} />
    ))}
  </dl>
);

const Section = ({ title, source, children }) => (
  <section className="crm-review-section">
    <div className="crm-review-section-heading">
      <h2>{title}</h2>
      {source && <span className="crm-review-source">{source}</span>}
    </div>
    {children}
  </section>
);

const Empty = () => <p className="crm-review-empty">No data available</p>;

const CrmSyncReview = ({
  flow = "crm",
  invitedName,
  dataSource,
  entityData,
  businessData,
  personData,
  error,
  busy,
  onConfirm,
  formatAddress,
  formatDate,
  formatYearMonth,
  showPrivacyNotice,
  onDismissPrivacyNotice,
}) => {
  const isCrm = flow === "crm";
  const isPropertyCoOwner = flow === "property-co-owner";
  const isCoOwner = flow === "co-owner" || isPropertyCoOwner;
  const errorCopy = error === "NAME_MISMATCH"
    ? `The name returned by MyInfo doesn’t match the co-owner we need to verify. Please use the Singpass account for ${invitedName || "the invited co-owner"}, or reopen the invitation email.`
    : error === "COOKIE_MISSING"
    ? "This confirmation link is incomplete or expired. Please open the link from your email invitation again."
    : isCrm
    ? "Please return to the CRM and start a new verification request."
    : "MyInfo is currently unavailable, please try again later. You can return to your account profile to enter your details manually.";
  const isCorpPass = dataSource === "corppass";
  const sourceName = isCorpPass ? "CorpPass" : "MyInfo";
  const business = entityData?.["basic-profile"];
  const vehicles = personData?.vehicles || [];
  const homes = personData?.hdbownership || [];
  const cpfWithdrawals = personData?.cpfhousingwithdrawal?.withdrawaldetails || [];
  const cpfContributions = personData?.cpfcontributions?.history || [];
  const noaHistory = personData?.noahistory?.noas || [];
  const cpfBalances = Object.entries(personData?.cpfbalances || {});

  return (
    <div className="crm-review-page">
      <header className="crm-review-header">
        <a className="crm-review-brand" href="/home" aria-label="SmartLend home">
          <span className="crm-review-mark" aria-hidden="true">S</span>
          <span>SmartLend</span>
        </a>
        <span className="crm-review-secure"><LockOutlinedIcon fontSize="inherit" /> Secure verification</span>
      </header>

      <main className="crm-review-main">
        {error ? (
          <div className="crm-review-status" role="alert">
            <span className="crm-review-status-icon"><ErrorOutlineIcon /></span>
            <p className="crm-review-kicker">Verification could not be completed</p>
            <h1>We couldn’t finish this verification</h1>
            <p>{errorCopy}</p>
          </div>
        ) : !(isCorpPass ? entityData || personData : personData) ? (
          <div className="crm-review-status" role="status" aria-live="polite">
            <CircularProgress className="crm-review-spinner" size={42} />
            <p className="crm-review-kicker">Secure verification</p>
            <h1>Preparing your details</h1>
            <p>We’re retrieving the information you agreed to share through Singpass.</p>
          </div>
        ) : (
          <>
            <div className="crm-review-intro">
              <p className="crm-review-kicker">{sourceName} verification</p>
              <h1>Review your details</h1>
              <p>{isCrm ? "Check the information shared through Singpass before continuing to the CRM." : isPropertyCoOwner ? "Review the Myinfo you want to share with this property loan case." : isCoOwner ? "Check your details before confirming your property co-ownership." : "Check the information shared through Singpass before saving it to your SmartLend profile."}</p>
              <ol className="crm-review-steps" aria-label="Verification progress">
                <li className="complete"><span>✓</span> Authenticate</li>
                <li className="current" aria-current="step"><span>2</span> Review details</li>
                <li><span>3</span> {isCrm ? "Return to CRM" : isPropertyCoOwner ? "Share Myinfo" : isCoOwner ? "Confirm ownership" : "Save to profile"}</li>
              </ol>
            </div>

            <div className="crm-review-layout">
              <div className="crm-review-details">
                {isCorpPass ? <BusinessDataReview data={businessData || { entity: entityData, person: personData }} /> : <>
                {business && (
                  <Section title="Business details" source="CorpPass">
                    <Fields items={[
                      ["UEN", business?.uen?.value],
                      ["Entity name", business?.["entity-name"]?.value],
                      ["Entity type", business?.["entity-type"]?.desc],
                      ["Entity status", business?.["entity-status"]?.value],
                      ["Registration date", business?.["registration-date"]?.value],
                      ["Primary activity", business?.["primary-activity"]?.desc],
                    ]} />
                  </Section>
                )}

                <Section title={isCorpPass ? "Authorised person" : "Personal details"} source={sourceName}>
                  <Fields items={[
                    ["Name", personData?.name?.value],
                    ["NRIC / FIN", personData?.uinfin?.value],
                    ["Date of birth", personData?.dob?.value],
                    ["Sex", personData?.sex?.desc],
                    ["Race", personData?.race?.desc],
                    ["Birth country", personData?.birthcountry?.desc],
                    ["Nationality", personData?.nationality?.desc],
                    ["Marital status", personData?.marital?.desc],
                    ["Occupation", personData?.occupation?.value],
                    ["Registered address", formatAddress(personData?.regadd)],
                    ["Email", personData?.email?.value],
                    ["Mobile number", (personData?.mobileno?.prefix?.value || "") +
                      (personData?.mobileno?.areacode?.value || "") +
                      (personData?.mobileno?.nbr?.value || "")],
                    ["Employment company", personData?.employment?.value],
                    ["Employment sector", personData?.employmentsector?.value],
                    ["Owner of private property", personData?.ownerprivate?.value ? "True" : "False"],
                    ["Residential status", personData?.residentialstatus?.desc],
                    ["HDB type", personData?.hdbtype?.desc],
                    ["Housing type", personData?.housingtype?.desc],
                  ]} />
                </Section>

                <Section title="Vehicle ownership">
                  {vehicles.length ? vehicles.map((vehicle, index) => (
                    <Fields key={index} items={[[`Vehicle ${index + 1} type`, vehicle?.type?.value]]} />
                  )) : <Empty />}
                </Section>

                <Section title="HDB ownership">
                  {homes.length ? homes.map((home, index) => (
                    <div className="crm-review-entry" key={index}>
                      <h3>Property {index + 1}</h3>
                      <Fields items={[
                        ["Date of purchase", home?.dateofpurchase?.value],
                        ["HDB type", home?.hdbtype?.desc],
                        ["Address", formatAddress(home?.address)],
                        ["Purchase price", money(home?.purchaseprice?.value)],
                        ["Outstanding loan balance", money(home?.outstandingloanbalance?.value)],
                        ["Monthly instalment", money(home?.monthlyloaninstalment?.value)],
                        ["Outstanding instalment", money(home?.outstandinginstalment?.value)],
                        ["Term of lease", `${home?.termoflease?.value || "-"} years`],
                        ["Number of owners", home?.noofowners?.value],
                        ["Date of ownership transfer", home?.dateofownershiptransfer?.value],
                      ]} />
                    </div>
                  )) : <Empty />}
                </Section>

                <Section title="CPF account details">
                  {cpfBalances.length ? <Fields items={cpfBalances.map(([account, balance]) => [
                    `CPF ${account.toUpperCase()} balance`, money(balance?.value),
                  ])} /> : <Empty />}
                </Section>

                <Section title="CPF housing withdrawal">
                  {cpfWithdrawals.length ? cpfWithdrawals.map((withdrawal, index) => (
                    <div className="crm-review-entry" key={index}>
                      <h3>Withdrawal {index + 1}</h3>
                      <Fields items={[
                        ["Address", formatAddress(withdrawal?.address)],
                        ["Principal withdrawal", money(withdrawal?.principalwithdrawalamt?.value)],
                        ["Accrued interest", money(withdrawal?.accruedinterestamt?.value)],
                        ["Monthly instalment", money(withdrawal?.monthlyinstalmentamt?.value)],
                        ["Total CPF allowed", money(withdrawal?.totalamountofcpfallowedforproperty?.value)],
                      ]} />
                    </div>
                  )) : <Empty />}
                </Section>

                <Section title="CPF contributions">
                  {cpfContributions.length ? cpfContributions.map((contribution, index) => (
                    <div className="crm-review-entry" key={index}>
                      <h3>Contribution {index + 1}</h3>
                      <Fields items={[
                        ["Employer", contribution?.employer?.value],
                        ["Paid on", formatDate(contribution?.date?.value)],
                        ["Contribution month", formatYearMonth(contribution?.month?.value)],
                        ["Amount", money(contribution?.amount?.value)],
                      ]} />
                    </div>
                  )) : <Empty />}
                </Section>

                <Section title="NOA history">
                  {noaHistory.length ? noaHistory.map((noa, index) => (
                    <div className="crm-review-entry" key={index}>
                      <h3>Assessment {index + 1}</h3>
                      <Fields items={[
                        ["Year of assessment", noa?.yearofassessment?.value],
                        ["Trade", money(noa?.trade?.value)],
                        ["Interest", money(noa?.interest?.value)],
                        ["Employment", money(noa?.employment?.value)],
                        ["Rent", money(noa?.rent?.value)],
                        ["Amount", money(noa?.amount?.value)],
                        ["Category", noa?.category?.value],
                      ]} />
                    </div>
                  )) : <Empty />}
                </Section>
                </>}
              </div>

              <aside className="crm-review-confirm">
                <CheckCircleOutlineIcon className="crm-review-confirm-icon" />
                <div className="crm-review-confirm-copy">
                  <h2>Ready to confirm?</h2>
                  <p>Make sure your information is accurate and up to date.</p>
                </div>
                <button type="button" onClick={onConfirm} disabled={busy}>
                  {busy ? "Saving..." : isPropertyCoOwner ? "Confirm & share Myinfo" : "Confirm & continue"}
                </button>
                <small>{isCrm ? "Your details will be shared with the CRM after confirmation." : isPropertyCoOwner ? "Confirming saves your Myinfo for SmartLend support to review. Lenders can see it only after support approves the scan." : isCoOwner ? "Confirming shares your details with SmartLend to verify co-ownership." : "Your details will be saved to your SmartLend profile after confirmation."}</small>
              </aside>
            </div>
          </>
        )}
      </main>
      {showPrivacyNotice && (
        <footer className="crm-review-footer">
          <span>Information is collected in accordance with our Privacy Policy. By continuing you agree to our terms.</span>
          <button type="button" aria-label="Dismiss privacy notice" onClick={onDismissPrivacyNotice}>
            <CloseIcon fontSize="small" />
          </button>
        </footer>
      )}
    </div>
  );
};

export default CrmSyncReview;
